import {
  SITE_COLOR,
  SITE_EASE,
  SITE_HEADER_HEIGHT_PX,
  SITE_MEDIA,
  SITE_SCROLL_PADDING_TOP_PX,
  SITE_VAR,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import {
  SITE_HERO_COMPACT_MARK_RIGHT,
  SITE_HERO_COMPACT_MARK_SIZE,
  SITE_HERO_LANDSCAPE_MARK_SIZE,
  SITE_HERO_MARK_SIZE,
  SITE_HERO_RISE_STYLES,
  SITE_HERO_TEXT_TOP,
  SITE_HERO_TOP,
  siteHeroTextTop,
  siteHeroTileShadow,
} from "../siteKit/siteHeroStyles";

// 이동해 도착하는 자리(service-manual 「매뉴얼 장 구성」)는 장·절의 scroll-margin 으로 정한다 — 해시 주소로 열 때도 같은 자리.
// 넓은 화면: 장은 왼쪽 장 정보가 붙는 높이에, 절은 제목이 장 이름 줄(장 정보 높이 + 오른쪽 시작 여백)에. 좁은 화면: 머리말 아래 24px
const SIDE_STICKY_TOP = "88px";
/** 넓은 화면 왼쪽 열 맨 아래 「전체 목차」·「처음으로」 줄이 화면 아래 끝에서 떨어지는 거리(24px 은 「너무 밑에 붙었다」) */
const SIDE_BOTTOM_GAP = "48px";
/** 목록 글자 — 흐린 ink3(대비 2.6:1)은 본문 글자 기준에 못 미쳐 ink2(5.7:1) */
const TOC_LINK = {
  fontSize: "14px",
  lineHeight: 1.45,
  color: SITE_COLOR.ink2,
  transition: "color .2s",
} as const;
/** 떠 있는 판(좁은 화면 아래 막대·펼친 목록)의 그림자 */
const FLOAT_SHADOW =
  "0 10px 32px rgba(28,44,66,.16),0 2px 6px rgba(28,44,66,.06)";
/**
 * 목록 카드가 나타나는 움직임(service-manual 「매뉴얼 장 구성」) — `hidden` 이 풀려 그려지기 시작할 때 아래에서 살짝 떠오르며 보인다.
 * 닫힐 때는 바로 사라진다(닫힌 목록이 초점·보조 기술에 잡히는 틈을 두지 않으려고 `hidden` 을 그대로 쓴다)
 */
const TOC_CARD_POP = {
  transformOrigin: "bottom center",
  animation: `manualTocCardPop .22s ${SITE_EASE.out}`,
  "@keyframes manualTocCardPop": {
    from: { opacity: 0, transform: "translateY(6px) scale(.98)" },
    to: { opacity: 1, transform: "none" },
  },
  [SITE_MEDIA.reducedMotion]: { animation: "none" },
} as const;
const BODY_TOP = "28px";
/**
 * 손님 안내 띠 — 화면 아래에 붙어 위는 비치고, 흐려지는 구간은 밝고 푸릇한 연파랑으로(사용자: 「밝은 톤」·「먹색보다 푸릇하게」 —
 * 흰빛만 덮으면 뒤 글자와 섞여 회색으로 보인다), 아래로 갈수록 `SITE_COLOR.blue100` 으로 덮는다(흰 바탕과 구분)
 */
const GUEST_FADE = `linear-gradient(rgba(255,255,255,0),rgba(236,245,255,.9) 30%,rgba(222,236,255,.97) 52%,${SITE_COLOR.blue100} 68%)`;
const GATE_VEIL_HEIGHT = "min(50vh,480px)";
/** 띠가 나타날 때 올라오는 거리 */
const GATE_VEIL_RISE = "translateY(16px)";
/** 띠가 보이는 상태 — 스크롤마다 바뀌므로 React 상태가 아니라 DOM 속성으로 둔다(`ManualGuestPreview` 가 단다) */
export const MANUAL_GATE_VISIBLE_ATTR = "data-visible";
const CHAPTER_PAD = "clamp(56px,7.2vw,104px)";
const TOPIC_PAD = "clamp(40px,4.8vw,64px)";
const NARROW_ARRIVAL = `${SITE_HEADER_HEIGHT_PX + 24}px`;
/** 장·절 위 구분선 두께 */
const RULE = "1px";

/**
 * 내용 첫 줄이 `line` 에 오게 하는 scroll-margin-top. 사이트 루트의 scroll-padding 에 더해지므로 그만큼 빼고, 구분선과
 * 위 여백(`pad`)을 지나 첫 줄이 시작한다. 구분선·위 여백이 없는 블록(장 첫 절)은 `pad` 를 주지 않는다.
 */
const arrivalMargin = (line: string, pad?: string) =>
  pad
    ? `calc(${line} - ${SITE_SCROLL_PADDING_TOP_PX}px - ${RULE} - ${pad})`
    : `calc(${line} - ${SITE_SCROLL_PADDING_TOP_PX}px)`;

/**
 * 매뉴얼(service-manual) — 첫 화면은 업데이트 소식 틀(왼쪽 제목·검색, 오른쪽 기운 앱 타일) + 여덟 장 두 열 줄,
 * 본문은 장마다 왼쪽 고정 열(번호·이름·그 장의 절 목록, 맨 아래 「전체 목차」·「처음으로」)과 오른쪽 절.
 * 좁은 화면은 왼쪽 열 대신 아래 막대(「전체 목차」·「처음으로」).
 */
/** 가로 터치 화면의 「전체 목차」 막대 폭 — 안내 플레이어 폭 계산(guideStyles)이 이 폭만큼 비켜 간다 */
export const TOC_BAR_LANDSCAPE_WIDTH = "320px";

export const MANUAL_STYLES = {
  page: { paddingBottom: "clamp(96px,12vw,192px)" },
  hero: {
    position: "relative",
    isolation: "isolate",
    paddingTop: SITE_HERO_TOP,
  },
  heroGrid: {
    ...SITE_WRAP,
    ...SITE_HERO_RISE_STYLES,
    display: "grid",
    gridTemplateColumns: `minmax(0,1fr) ${SITE_HERO_MARK_SIZE}`,
    gap: "clamp(32px,6vw,80px)",
    alignItems: "start",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr", gap: 0 },
    // 가로 터치 화면: 넓은 화면처럼 글 오른쪽 열에 화면 높이에 맞춘 타일
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: `minmax(0,1fr) ${SITE_HERO_LANDSCAPE_MARK_SIZE}`,
      gap: "clamp(24px,4vw,48px)",
    },
  },
  // 제목은 업데이트 소식 첫 화면과 같은 높이에서 시작한다
  heroText: {
    minWidth: 0,
    paddingTop: SITE_HERO_TEXT_TOP,
    [SITE_MEDIA.tablet]: { paddingTop: 0 },
    [SITE_MEDIA.landscapeTouch]: {
      paddingTop: siteHeroTextTop(SITE_HERO_LANDSCAPE_MARK_SIZE),
    },
  },
  // 앱 타일 자리 — 크기는 업데이트 소식 타일과 같은 규칙(칸 너비). 자리는 움직이지 않고 바닥 그림자를 진다(업데이트 소식과 같은 그림자, 가운데에서 타일 폭 4% 왼쪽)
  mark: {
    ...siteHeroTileShadow("-4%"),
    position: "relative",
    justifySelf: "end",
    marginRight: "-8px",
    width: "100%",
    aspectRatio: "1",
    pointerEvents: "none",
    // 휴대폰: 업데이트 소식처럼 타일을 오른쪽 위에, 글 뒤로. 타일을 28% 올리므로 그림자도 그만큼 올리고, 타일이 멈춰 있어 그림자도 멈춘다
    [SITE_MEDIA.tablet]: {
      position: "absolute",
      right: SITE_HERO_COMPACT_MARK_RIGHT,
      top: SITE_HERO_TOP,
      width: SITE_HERO_COMPACT_MARK_SIZE,
      zIndex: -1,
      justifySelf: "auto",
      marginRight: 0,
      animation: "none",
      "&::before": { top: "75%", animation: "none" },
    },
    // 가로 터치 화면: 글 오른쪽 열에 다시 놓는다(타일을 올리지 않으므로 그림자는 타일 아래). 좁은 화면처럼 멈춰 있다
    [SITE_MEDIA.landscapeTouch]: {
      position: "relative",
      right: "auto",
      top: "auto",
      width: "100%",
      zIndex: "auto",
      justifySelf: "end",
      marginRight: "-8px",
      "&::before": { top: "103%", animation: "none" },
    },
  },
  // 기운 앱 타일: 남색 빛 바탕 + 금색 안내 그림(로고 남색·노랑). 업데이트 소식 타일처럼 위가 밝고 아래로 빛이 은은하게 번지되,
  // 색(남색·금색)과 기울기(업데이트 소식과 거울 대칭 — 그쪽 −6°, 이쪽 +6°)로 구분한다. 떠 있는 폭·박자도 업데이트 소식과 같다
  markTile: {
    position: "absolute",
    inset: 0,
    borderRadius: "26%",
    background: "linear-gradient(150deg,#EEF2FD 0%,#D2DCF6 55%,#B1C2EC 100%)",
    boxShadow:
      "0 34px 76px -22px rgba(64,96,200,.5),0 0 0 1px rgba(64,96,200,.1),inset 0 2px 0 #fff",
    transform: "rotate(6deg)",
    // 등장은 자리(mark)가 첫 화면 글과 함께 하고, 타일은 바닥 그림자와 같은 박자로 떠 있기만 한다
    animation: "manualMarkFloat 8s ease-in-out infinite alternate",
    "@keyframes manualMarkFloat": {
      from: { transform: "translateY(-4%) rotate(7deg)" },
      to: { transform: "translateY(4%) rotate(4deg)" },
    },
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "inherit",
      background:
        "linear-gradient(180deg,rgba(255,255,255,.6),rgba(255,255,255,0) 60%)",
    },
    "& img": {
      position: "absolute",
      left: "9%",
      top: "14%",
      width: "82%",
      height: "72%",
      display: "block",
      objectFit: "contain",
    },
    [SITE_MEDIA.reducedMotion]: { animation: "none" },
    [SITE_MEDIA.tablet]: {
      transform: "translateY(-28%) rotate(6deg)",
      animation: "none",
    },
    [SITE_MEDIA.landscapeTouch]: {
      transform: "rotate(6deg)",
      animation: "none",
    },
  },
  // 여덟 장 두 열 줄: 번호·이름·장 제목만, 화살표는 올렸을 때만 파랗게
  chapterLinesWrap: SITE_WRAP,
  chapterLines: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0 clamp(48px,8.3vw,120px)",
    marginTop: "clamp(72px,8.9vw,128px)",
    borderTop: `1px solid ${SITE_COLOR.line}`,
    listStyle: "none",
    padding: 0,
    "& a, & [data-row]": {
      display: "grid",
      gridTemplateColumns: "44px minmax(0,1fr) 18px",
      gridTemplateRows: "auto auto",
      columnGap: "16px",
      alignItems: "center",
      padding: "clamp(22px,2.1vw,30px) 0",
      borderBottom: `1px solid ${SITE_COLOR.line}`,
      color: SITE_COLOR.ink,
    },
    // 손님의 장 줄 — 누를 수 없는 글자라 조금 흐리게
    "& [data-row]": { color: SITE_COLOR.ink2 },
    "& [data-n]": {
      gridColumn: 1,
      gridRow: "1/3",
      alignSelf: "start",
      paddingTop: "4px",
      fontSize: "14px",
      fontWeight: 600,
      color: SITE_COLOR.ink3,
      fontVariantNumeric: "tabular-nums",
    },
    "& b": {
      gridColumn: 2,
      gridRow: 1,
      fontSize: "clamp(18px,1.46vw,21px)",
      fontWeight: 700,
      letterSpacing: "-.02em",
      lineHeight: 1.3,
      transition: "color .15s",
    },
    "& [data-sub]": {
      gridColumn: 2,
      gridRow: 2,
      marginTop: "6px",
      fontSize: "15px",
      lineHeight: 1.5,
      color: SITE_COLOR.ink2,
    },
    "& svg": {
      gridColumn: 3,
      gridRow: "1/3",
      color: SITE_COLOR.line,
      transition: `color .15s,transform .2s ${SITE_EASE.out}`,
    },
    "& a:hover b, & a:hover svg": { color: SITE_COLOR.blue },
    "& a:hover svg": { transform: "translateX(3px)" },
    [SITE_MEDIA.tablet]: {
      gridTemplateColumns: "1fr",
      gap: 0,
      marginTop: "clamp(56px,10vw,80px)",
      "& a, & [data-row]": { padding: "20px 0" },
    },
  },
  doc: { ...SITE_WRAP, marginTop: SITE_VAR.block },
  /** 로그인 여부를 확인하는 동안 — 장·절 자리만 비워 둬 푸터가 튀지 않게 */
  checking: { minHeight: "60vh" },
  chapter: {
    display: "grid",
    gridTemplateColumns: "minmax(0,220px) minmax(0,1fr)",
    gap: "clamp(32px,5vw,96px)",
    paddingBlock: CHAPTER_PAD,
    borderTop: `${RULE} solid ${SITE_COLOR.line}`,
    scrollMarginTop: arrivalMargin(SIDE_STICKY_TOP, CHAPTER_PAD),
    // 이동한 자리로 초점만 옮겨 둔 것이라(누르는 요소가 아님) 테두리를 그리지 않는다
    "&:focus-visible": { outline: "none" },
    "&:last-of-type": { paddingBottom: 0 },
    [SITE_MEDIA.tablet]: {
      gridTemplateColumns: "1fr",
      gap: "20px",
      scrollMarginTop: arrivalMargin(NARROW_ARRIVAL, CHAPTER_PAD),
    },
  },
  // 넓은 화면: 화면 높이를 채우는 세로 띠 — 위는 장 정보·절 목록(넘치면 그 칸만 스크롤), 맨 아래는 「전체 목차」·「처음으로」
  side: {
    position: "sticky",
    top: SIDE_STICKY_TOP,
    alignSelf: "start",
    height: `calc(100vh - ${SIDE_STICKY_TOP} - ${SIDE_BOTTOM_GAP})`,
    display: "flex",
    flexDirection: "column",
    [SITE_MEDIA.tablet]: {
      position: "static",
      height: "auto",
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "baseline",
      gap: "4px 10px",
    },
  },
  chapterNumber: {
    fontSize: "15px",
    fontWeight: 600,
    color: SITE_COLOR.ink3,
    fontVariantNumeric: "tabular-nums",
  },
  chapterName: {
    fontSize: "clamp(22px,2vw,28px)",
    fontWeight: 700,
    letterSpacing: "-.03em",
    lineHeight: 1.3,
    marginTop: "4px",
    "& a:hover": { color: SITE_COLOR.blueDark },
    // 휴대폰에서도 장 이름(26) > 절 제목(20) — 큰 제목은 하나로 보이게
    [SITE_MEDIA.tablet]: { marginTop: 0, fontSize: "26px" },
  },
  toc: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    overscrollBehavior: "contain",
    scrollbarWidth: "thin",
    listStyle: "none",
    // 넘쳐 스크롤되는 칸이 초점 테두리를 자르지 않게 좌우로 조금 넓힌다
    margin: "20px -4px 0",
    padding: "0 4px",
    "& a": {
      ...TOC_LINK,
      display: "block",
      padding: "6px 0",
      fontSize: "15px",
    },
    "& a:hover": { color: SITE_COLOR.ink },
    '& a[aria-current="true"]': { color: SITE_COLOR.blueDark, fontWeight: 600 },
    [SITE_MEDIA.tablet]: { display: "none" },
  },
  // 왼쪽 열 맨 아래 — 줄은 그 자리에 고정, 「전체 목차」를 펼치면 장 목록 카드가 줄 위에 뜬다(절 목록 칸은 그대로, 카드가 위에 겹친다).
  // DOM 은 「단추 줄 → 장 목록」(Tab 이 단추에서 목록으로 이어지게)
  sideFoot: {
    flex: "none",
    position: "relative",
    marginTop: "20px",
    paddingTop: "8px",
    borderTop: `1px solid ${SITE_COLOR.line}`,
    [SITE_MEDIA.tablet]: { display: "none" },
  },
  sideActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  allChaptersToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "40px",
    padding: 0,
    border: 0,
    background: "none",
    font: "inherit",
    fontSize: "14px",
    fontWeight: 600,
    color: SITE_COLOR.ink2,
    cursor: "pointer",
    transition: "color .2s",
    "&:hover": { color: SITE_COLOR.ink },
    "& [data-chevron]": { transition: "transform .2s" },
    '&[aria-expanded="true"] [data-chevron]': { transform: "rotate(180deg)" },
  },
  backToStart: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "40px",
    fontSize: "14px",
    fontWeight: 600,
    color: SITE_COLOR.ink2,
    transition: "color .2s",
    "&:hover": { color: SITE_COLOR.ink },
  },
  // 「전체 목차」 드롭다운 카드 — 좁은 화면 막대 카드와 같은 모양. 글자 시작은 열의 글자 줄(왼쪽 0)에 맞춘다(-16 + 테두리 1 + 안쪽 6 + 줄 9)
  sideChapterList: {
    ...TOC_CARD_POP,
    position: "absolute",
    left: "-16px",
    right: "-16px",
    bottom: "calc(100% + 8px)",
    zIndex: 2,
    maxHeight: `calc(100vh - ${SIDE_STICKY_TOP} - ${SIDE_BOTTOM_GAP} - 64px)`,
    overflowY: "auto",
    overscrollBehavior: "contain",
    listStyle: "none",
    margin: 0,
    padding: "6px",
    borderRadius: "14px",
    background: SITE_COLOR.bg,
    border: `1px solid ${SITE_COLOR.line2}`,
    boxShadow: FLOAT_SHADOW,
    "&[hidden]": { display: "none" },
    "& a": {
      ...TOC_LINK,
      display: "flex",
      alignItems: "baseline",
      gap: "10px",
      padding: "7px 9px",
      borderRadius: "8px",
    },
    "& [data-n]": {
      flex: "none",
      width: "22px",
      fontSize: "12.5px",
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
    },
    "& a:hover": { color: SITE_COLOR.ink, background: SITE_COLOR.hoverTint },
    '& a[aria-current="true"]': {
      color: SITE_COLOR.blueDark,
      fontWeight: 600,
      background: SITE_COLOR.blue50,
    },
  },
  // 오른쪽은 첫 절 제목을 장 이름 줄에 맞춰 시작(장 번호 줄만큼 내림)
  body: {
    minWidth: 0,
    paddingTop: BODY_TOP,
    [SITE_MEDIA.tablet]: { paddingTop: 0 },
  },
  topic: {
    paddingBlock: TOPIC_PAD,
    // 절 제목 아래 단계별 안내 플레이어까지의 간격
    "& > figure": { marginTop: "clamp(20px,2.4vw,32px)" },
    borderTop: `${RULE} solid ${SITE_COLOR.line2}`,
    scrollMarginTop: arrivalMargin(
      `${SIDE_STICKY_TOP} + ${BODY_TOP}`,
      TOPIC_PAD
    ),
    "&:focus-visible": { outline: "none" },
    "&:first-of-type": {
      borderTop: 0,
      paddingTop: 0,
      scrollMarginTop: arrivalMargin(`${SIDE_STICKY_TOP} + ${BODY_TOP}`),
    },
    "&:last-of-type": { paddingBottom: 0 },
    [SITE_MEDIA.tablet]: {
      scrollMarginTop: arrivalMargin(NARROW_ARRIVAL, TOPIC_PAD),
      "&:first-of-type": { scrollMarginTop: arrivalMargin(NARROW_ARRIVAL) },
    },
  },
  topicTitle: {
    fontSize: "clamp(21px,1.8vw,24px)",
    fontWeight: 700,
    letterSpacing: "-.022em",
    lineHeight: 1.4,
    marginBottom: "12px",
    [SITE_MEDIA.tablet]: { fontSize: "20px" },
  },
  // 그 장 화면을 여는 법 — 장 첫 절 제목 바로 아래 첫 문단(따로 떨어진 안내 단락을 두지 않는다)
  topicIntro: {
    margin: 0,
    fontSize: "15px",
    lineHeight: 1.7,
    color: SITE_COLOR.ink2,
    maxWidth: "52em",
    textWrap: "pretty",
  },
  // 참고: 회색 상자 대신 가는 왼쪽 선 + 라벨만
  note: {
    marginTop: "clamp(20px,2.2vw,28px)",
    padding: "2px 0 2px 18px",
    borderLeft: `2px solid ${SITE_COLOR.line}`,
    fontSize: "15px",
    lineHeight: 1.7,
    color: SITE_COLOR.ink2,
    maxWidth: "52em",
    textWrap: "pretty",
    "& > b, & > p > b": {
      color: SITE_COLOR.ink,
      fontWeight: 600,
      marginRight: "8px",
    },
  },
  /** 매핑 절 참고 안의 가이드 진입 — 안내 문장 사이에 둔다 */
  guideEntry: { margin: "8px 0 10px -8px" },
  // 손님(service-manual 「로그인하지 않은 방문자의 매뉴얼」): 내용은 01장 첫 절 맛보기에서 끝나고, 화면 아래에 붙은 전체 너비
  // 연한 파란 띠(넓은 화면은 첫 화면 목차 가운데를 지나면, 좁은 화면은 처음부터 보임) 안에 안내가 있다. 띠 뒤에는 바로 푸터
  guestPage: { paddingBottom: 0 },
  /** 손님의 왼쪽 열 — 붙어 있을 본문이 없으니 화면 높이로 늘리지 않는다(늘리면 맛보기 아래가 빈다) */
  guestSide: { position: "static", height: "auto" },
  guestToc: {
    margin: "20px 0 0",
    padding: 0,
    listStyle: "none",
    "& li": { ...TOC_LINK, padding: "6px 0", fontSize: "15px" },
    [SITE_MEDIA.tablet]: { display: "none" },
  },
  guestTopic: {
    maxHeight: "clamp(360px,46vw,560px)",
    overflow: "hidden",
    userSelect: "none",
  },
  gateVeil: {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    height: GATE_VEIL_HEIGHT,
    marginTop: `calc(-1 * ${GATE_VEIL_HEIGHT})`,
    display: "grid",
    placeItems: "end center",
    paddingInline: SITE_VAR.gutter,
    // 안내 아래는 푸터와 넉넉히 떨어진다(사용자 세 차례 「더」)
    paddingBottom: "clamp(112px,15.5vh,176px)",
    background: GUEST_FADE,
    // 넓은 화면은 첫 화면에서 숨어 있다가 목차 가운데가 화면 아래 1/4 선까지 올라오면(`MANUAL_GATE_VISIBLE_ATTR`) 떠오른다. 안에 초점이 있으면 늘 보인다
    opacity: 0,
    transform: GATE_VEIL_RISE,
    transition: `opacity .3s ${SITE_EASE.out}, transform .3s ${SITE_EASE.out}`,
    // 띠 자체는 뒤 내용을 가릴 뿐 누르는 것이 아니다 — 보일 때 안내만 받는다
    pointerEvents: "none",
    [`&[${MANUAL_GATE_VISIBLE_ATTR}], &:focus-within`]: {
      opacity: 1,
      transform: "none",
      "& > *": { pointerEvents: "auto" },
    },
    // 좁은 화면(휴대폰·세로 태블릿)은 첫 화면부터 늘 보인다 — 한 화면에 목차가 다 들어오지 않아 기다릴 까닭이 없다
    [SITE_MEDIA.tablet]: {
      opacity: 1,
      transform: "none",
      "& > *": { pointerEvents: "auto" },
    },
    [SITE_MEDIA.reducedMotion]: { transition: "none" },
  },
  // 안내는 상자 없이 글자·버튼만(흰 바탕 위 흰 상자는 묻힌다 — 사용자 V2 선택)
  gate: {
    width: "min(640px,100%)",
    textAlign: "center",
    "&:focus-visible": { outline: "none" },
  },
  gateTitle: {
    fontSize: "clamp(24px,2.2vw,30px)",
    fontWeight: 700,
    letterSpacing: "-.025em",
    lineHeight: 1.36,
    textWrap: "balance",
  },
  gateText: {
    margin: "12px 0 0",
    fontSize: "16px",
    lineHeight: 1.6,
    color: SITE_COLOR.ink2,
    textWrap: "pretty",
  },
  gateActions: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "22px",
  },
  // 좁은 화면 아래 막대 — 장들이 화면에 있을 때만(data-on). 「전체 목차」가 남는 폭을 다 쓰고 「처음으로」는 오른쪽(오른손 엄지 자리)
  tocBar: {
    display: "none",
    [SITE_MEDIA.tablet]: {
      display: "block",
      position: "fixed",
      left: "50%",
      bottom: "calc(12px + env(safe-area-inset-bottom,0px))",
      zIndex: 55,
      width: "calc(100% - 32px)",
      maxWidth: "560px",
      transform: "translate3d(-50%,calc(100% + 24px),0)",
      opacity: 0,
      visibility: "hidden",
      transition: `transform .5s ${SITE_EASE.out},opacity .3s ease,visibility 0s linear .5s`,
      "&[data-on]": {
        transform: "translate3d(-50%,0,0)",
        opacity: 1,
        visibility: "visible",
        transition: `transform .5s ${SITE_EASE.out},opacity .3s ease,visibility 0s`,
      },
    },
    // 가로 터치 화면: 화면 아래 가운데를 가로지르면 낮은 화면에서 안내 플레이어를 가린다 — 오른쪽 아래 작은 묶음으로
    [SITE_MEDIA.landscapeTouch]: {
      left: "auto",
      right: "calc(16px + env(safe-area-inset-right,0px))",
      width: TOC_BAR_LANDSCAPE_WIDTH,
      transform: "translate3d(0,calc(100% + 24px),0)",
      "&[data-on]": { transform: "translate3d(0,0,0)" },
    },
    [SITE_MEDIA.reducedMotion]: {
      transition: "none",
      "&[data-on]": { transition: "none" },
    },
  },
  tocBarRow: {
    display: "flex",
    alignItems: "stretch",
    height: "52px",
    overflow: "hidden",
    borderRadius: "14px",
    background: "rgba(255,255,255,.94)",
    border: `1px solid ${SITE_COLOR.line2}`,
    boxShadow: FLOAT_SHADOW,
    "@supports (backdrop-filter:blur(8px))": {
      background: "rgba(255,255,255,.82)",
      backdropFilter: "saturate(180%) blur(16px)",
    },
  },
  tocBarToggle: {
    flex: "1 1 auto",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "0 18px",
    border: 0,
    background: "none",
    font: "inherit",
    fontSize: "15px",
    fontWeight: 600,
    color: SITE_COLOR.ink,
    cursor: "pointer",
    "&:hover": { background: SITE_COLOR.hoverTint },
    "& [data-chevron]": { marginLeft: "auto", transition: "transform .2s" },
    '&[aria-expanded="true"] [data-chevron]': { transform: "rotate(180deg)" },
  },
  tocBarTop: {
    flex: "none",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "0 18px",
    borderLeft: `1px solid ${SITE_COLOR.line2}`,
    fontSize: "15px",
    fontWeight: 600,
    color: SITE_COLOR.ink2,
    "&:hover": { background: SITE_COLOR.hoverTint, color: SITE_COLOR.ink },
  },
  // 막대 위로 펼치는 목록 — 장 줄 44px·절 줄 40px(손가락 크기), 지금 장만 절을 펼친다
  barChapterList: {
    ...TOC_CARD_POP,
    position: "absolute",
    left: 0,
    right: 0,
    bottom: "calc(100% + 8px)",
    maxHeight: "min(64svh, 560px)",
    overflowY: "auto",
    overscrollBehavior: "contain",
    listStyle: "none",
    margin: 0,
    padding: "8px",
    borderRadius: "14px",
    background: SITE_COLOR.bg,
    border: `1px solid ${SITE_COLOR.line2}`,
    boxShadow: FLOAT_SHADOW,
    "&[hidden]": { display: "none" },
    "& > li > a": {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      minHeight: "44px",
      padding: "0 12px",
      borderRadius: "10px",
      fontSize: "15px",
      fontWeight: 600,
      color: SITE_COLOR.ink2,
    },
    "& [data-n]": {
      flex: "none",
      width: "24px",
      fontSize: "13px",
      fontVariantNumeric: "tabular-nums",
    },
    '& > li > a[aria-current="true"]': { color: SITE_COLOR.blueDark },
    "& ol": { listStyle: "none", margin: "0 0 6px", padding: "0 0 0 36px" },
    "& ol a": {
      display: "flex",
      alignItems: "center",
      minHeight: "40px",
      padding: "0 12px",
      borderRadius: "8px",
      fontSize: "14px",
      color: SITE_COLOR.ink2,
    },
    '& ol a[aria-current="true"]': {
      color: SITE_COLOR.blueDark,
      fontWeight: 600,
      background: SITE_COLOR.blue50,
    },
    "& a:hover": { background: SITE_COLOR.hoverTint },
  },
} as const;

/** 문구 안 화면 단추 이름(`<ui>`) — 회색 바탕 작은 칸 */
export const MANUAL_UI_LABEL_STYLES = {
  display: "inline-block",
  fontWeight: 600,
  color: SITE_COLOR.ink,
  background: SITE_COLOR.panel,
  border: "1px solid rgba(0,0,0,.08)",
  borderRadius: "6px",
  padding: "0 7px",
  fontSize: ".92em",
  lineHeight: 1.6,
  whiteSpace: "nowrap",
} as const;
